/**
 * Profile API Service
 * Handles all dating profile-related API calls.
 */

import { apiService } from './apiService';
import type {
  DatingProfile,
  CreateProfileRequest,
  UpdateProfileRequest,
  WizardStageSubmission,
  CompletionStatus,
  InterestCategory,
  UserInterest,
  PromptTemplate,
  UserPromptAnswer,
  UserPrompt,
  ProfilePhoto,
  ProfileVideo,
} from '../../types/profile';
import type {
  ProfileCard,
  ProfileCardField,
  FieldVisibilityUpdate,
} from '../../types/social';

// ==========================================
// PROFILE CRUD
// ==========================================

/**
 * Get current user's dating profile
 */
export async function getMyProfile(): Promise<DatingProfile | null> {
  const response = await apiService.get<DatingProfile>('/api/profile');
  if (response.error) {
    console.error('Failed to get profile:', response.error);
    return null;
  }
  return response.data || null;
}

/**
 * Create a new dating profile
 */
export async function createProfile(data: CreateProfileRequest): Promise<DatingProfile | null> {
  const response = await apiService.post<DatingProfile>('/api/profile', data);
  if (response.error) {
    console.error('Failed to create profile:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

/**
 * Update dating profile
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<DatingProfile | null> {
  const response = await apiService.put<DatingProfile>('/api/profile', data);
  if (response.error) {
    console.error('Failed to update profile:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

/**
 * Get another user's profile
 */
export async function getProfile(profileId: string): Promise<DatingProfile | null> {
  const response = await apiService.get<DatingProfile>(`/api/profile/${profileId}`);
  if (response.error) {
    console.error('Failed to get profile:', response.error);
    return null;
  }
  return response.data || null;
}

/**
 * Get profile completion status
 */
export async function getCompletionStatus(): Promise<CompletionStatus | null> {
  const response = await apiService.get<CompletionStatus>('/api/profile/completion');
  if (response.error) {
    console.error('Failed to get completion status:', response.error);
    return null;
  }
  return response.data || null;
}

// ==========================================
// WIZARD
// ==========================================

interface WizardSubmitResponse {
  stage: number;
  step: number;
  completionPercentage: number;
  allCompleted: boolean;
}

/**
 * Submit wizard stage data
 */
export async function submitWizardStage(
  stage: number,
  submission: WizardStageSubmission
): Promise<WizardSubmitResponse | null> {
  const response = await apiService.post<WizardSubmitResponse>(
    `/api/profile/wizard/${stage}`,
    submission
  );
  if (response.error) {
    console.error('Failed to submit wizard stage:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

// ==========================================
// INTERESTS
// ==========================================

/**
 * Get all interest categories with their interests
 */
export async function getInterestCategories(): Promise<InterestCategory[]> {
  const response = await apiService.get<InterestCategory[]>('/api/profile/interests/categories');
  if (response.error) {
    console.error('Failed to get interest categories:', response.error);
    return [];
  }
  return response.data || [];
}

/**
 * Get user's selected interests
 */
export async function getMyInterests(): Promise<UserInterest[]> {
  const response = await apiService.get<UserInterest[]>('/api/profile/interests');
  if (response.error) {
    console.error('Failed to get interests:', response.error);
    return [];
  }
  return response.data || [];
}

/**
 * Update user's interests
 */
export async function updateInterests(
  interestIds: number[],
  rankedInterestIds?: number[]
): Promise<boolean> {
  const response = await apiService.put('/api/profile/interests', {
    interestIds,
    rankedInterestIds,
  });
  if (response.error) {
    console.error('Failed to update interests:', response.error);
    return false;
  }
  return true;
}

// ==========================================
// PROMPTS
// ==========================================

/**
 * Get available prompt templates
 */
export async function getPromptTemplates(category?: string): Promise<PromptTemplate[]> {
  const endpoint = category
    ? `/api/profile/prompts/templates?category=${category}`
    : '/api/profile/prompts/templates';
  const response = await apiService.get<PromptTemplate[]>(endpoint);
  if (response.error) {
    console.error('Failed to get prompt templates:', response.error);
    return [];
  }
  return response.data || [];
}

/**
 * Get user's answered prompts
 */
export async function getMyPrompts(): Promise<UserPromptAnswer[]> {
  const response = await apiService.get<UserPromptAnswer[]>('/api/profile/prompts');
  if (response.error) {
    console.error('Failed to get prompts:', response.error);
    return [];
  }
  return response.data || [];
}

interface AddPromptResponse {
  id: string;
  promptId: number;
  answer: string;
  sortOrder: number;
}

/**
 * Add or update a prompt answer
 */
export async function addPromptAnswer(
  promptId: number,
  answer: string
): Promise<AddPromptResponse | null> {
  const response = await apiService.post<AddPromptResponse>('/api/profile/prompts', {
    promptId,
    answer,
  });
  if (response.error) {
    console.error('Failed to add prompt answer:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

/**
 * Update a prompt answer
 */
export async function updatePromptAnswer(
  promptAnswerId: string,
  answer: string
): Promise<{ id: string; answer: string } | null> {
  const response = await apiService.put<{ id: string; answer: string }>(
    `/api/profile/prompts/${promptAnswerId}`,
    { answer }
  );
  if (response.error) {
    console.error('Failed to update prompt answer:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

/**
 * Delete a prompt answer
 */
export async function deletePromptAnswer(promptAnswerId: string): Promise<boolean> {
  const response = await apiService.delete(`/api/profile/prompts/${promptAnswerId}`);
  if (response.error) {
    console.error('Failed to delete prompt answer:', response.error);
    return false;
  }
  return true;
}

// ==========================================
// PHOTOS
// ==========================================

/**
 * Upload a profile photo
 */
export async function uploadPhoto(file: File): Promise<ProfilePhoto | null> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiService.post<ProfilePhoto>('/api/profile/photos', formData);
  if (response.error) {
    console.error('Failed to upload photo:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

/**
 * Delete a profile photo
 */
export async function deletePhoto(photoId: string): Promise<boolean> {
  const response = await apiService.delete(`/api/profile/photos/${photoId}`);
  if (response.error) {
    console.error('Failed to delete photo:', response.error);
    return false;
  }
  return true;
}

/**
 * Reorder photos
 */
export async function reorderPhotos(photoIds: string[]): Promise<boolean> {
  const response = await apiService.put('/api/profile/photos/reorder', { photoIds });
  if (response.error) {
    console.error('Failed to reorder photos:', response.error);
    return false;
  }
  return true;
}

/**
 * Set primary photo
 */
export async function setPhotoAsPrimary(photoId: string): Promise<boolean> {
  const response = await apiService.put(`/api/profile/photos/${photoId}/primary`, {});
  if (response.error) {
    console.error('Failed to set primary photo:', response.error);
    return false;
  }
  return true;
}

// ==========================================
// VIDEOS (placeholders for future implementation)
// ==========================================

/**
 * Upload a profile video
 */
export async function uploadVideo(file: File): Promise<ProfileVideo | null> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiService.post<ProfileVideo>('/api/profile/videos', formData);
  if (response.error) {
    console.error('Failed to upload video:', response.error);
    throw new Error(response.error);
  }
  return response.data || null;
}

/**
 * Delete a profile video
 */
export async function deleteVideo(videoId: string): Promise<boolean> {
  const response = await apiService.delete(`/api/profile/videos/${videoId}`);
  if (response.error) {
    console.error('Failed to delete video:', response.error);
    return false;
  }
  return true;
}

// ==========================================
// PROFILE CARD & VISIBILITY (social.profile_card)
// ==========================================

/**
 * Viewer-scoped profile card. The server filters per-field visibility
 * before returning, so the caller never receives fields it isn't entitled
 * to (public / connections / private is resolved server-side).
 */
export async function getProfileCard(profileId: string): Promise<ProfileCard | null> {
  const response = await apiService.get<ProfileCard>(`/api/profile/${profileId}/card`);
  if (response.error) {
    console.error('Failed to get profile card:', response.error);
    return null;
  }
  return response.data || null;
}

/**
 * Same viewer-scoped card, but keyed by USER id instead of profile Guid.
 * The in-game surface only knows Unity's playerId (= user id), so this
 * avoids a resolve round-trip.
 */
export async function getProfileCardByUserId(userId: string): Promise<ProfileCard | null> {
  const response = await apiService.get<ProfileCard>(`/api/profile/user/${userId}/card`);
  if (response.error) {
    console.error('Failed to get profile card by user:', response.error);
    return null;
  }
  return response.data || null;
}

/** Owner-only: current per-field visibility tiers for the edit UI. */
export async function getFieldVisibility(): Promise<ProfileCardField[]> {
  const response = await apiService.get<ProfileCardField[]>('/api/profile/visibility');
  if (response.error) {
    console.error('Failed to get field visibility:', response.error);
    return [];
  }
  return response.data || [];
}

/** Owner-only: update per-field visibility tiers. Throws on failure so the
 * edit flow can surface an explicit save-error state (never silently drop). */
export async function updateFieldVisibility(
  fields: FieldVisibilityUpdate[]
): Promise<boolean> {
  const response = await apiService.put('/api/profile/visibility', { fields });
  if (response.error) {
    console.error('Failed to update field visibility:', response.error);
    throw new Error(response.error);
  }
  return true;
}

// ==========================================
// ALIASES (for wizard component compatibility)
// ==========================================

// Profile aliases
export const getProfile2 = getMyProfile;
export const getUserInterests = getMyInterests;
export const updateUserInterests = (interestIds: string[]): Promise<boolean> =>
  updateInterests(interestIds.map((id) => parseInt(id, 10)));
export const getUserPrompts = getMyPrompts;
export const addUserPrompt = (
  templateId: string,
  answer: string
): Promise<UserPrompt | null> =>
  addPromptAnswer(parseInt(templateId, 10), answer).then((res) =>
    res
      ? {
          id: res.id,
          templateId,
          answer: res.answer,
          sortOrder: res.sortOrder,
        }
      : null
  );
export const updateUserPrompt = updatePromptAnswer;
export const deleteUserPrompt = deletePromptAnswer;

// ==========================================
// EXPORT ALL
// ==========================================

export const profileService = {
  // Profile
  getMyProfile,
  createProfile,
  updateProfile,
  getProfile: getMyProfile, // Alias for wizard
  getCompletionStatus,

  // Wizard
  submitWizardStage,

  // Interests
  getInterestCategories,
  getMyInterests,
  updateInterests,
  getUserInterests: getMyInterests, // Alias for wizard
  updateUserInterests, // Alias for wizard

  // Prompts
  getPromptTemplates,
  getMyPrompts,
  addPromptAnswer,
  updatePromptAnswer,
  deletePromptAnswer,
  getUserPrompts: getMyPrompts, // Alias for wizard
  addUserPrompt, // Alias for wizard
  updateUserPrompt: updatePromptAnswer, // Alias for wizard
  deleteUserPrompt: deletePromptAnswer, // Alias for wizard

  // Photos
  uploadPhoto,
  deletePhoto,
  reorderPhotos,
  setPhotoAsPrimary,

  // Videos
  uploadVideo,
  deleteVideo,

  // Profile card & visibility (social.profile_card)
  getProfileCard,
  getProfileCardByUserId,
  getFieldVisibility,
  updateFieldVisibility,
};
